// src/pages/Sati.jsx

import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useDeferredValue,
  useRef,
  useCallback,
  startTransition,
} from 'react';
import { AuthContext } from '../config/AuthProvider';
import { useLoading } from '../config/LoadingContext';
import Login from '../authPage/Login';
import FirmManager from '../components/FirmManager';
import PersonFormSati from '../components/PersonFormSati';
import HoursTable from '../components/HoursTable';
import {
  getWeeksInYear,
  getCurrentWeekIndex,
  getWeekIndicesForCalendarMonth,
} from '../utils/weekUtils';
import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { parseGlovoExcel, parseWoltExcel } from '../utils/excelHoursParser';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaFileExport } from 'react-icons/fa';
import Modal from 'react-modal';
import { formatDecimalHoursSigned } from '../utils/durationUtils';
import { fetchDailyMetricsForCalendarYear } from '../utils/dailyMetricsFetch';
import {
  getHoursFromMetric,
  getWorkHoursFromMetric,
  isGodisnjiMetric,
  parseGodisnjiHours,
  parseGlovoHours,
  parseWoltHours,
} from '../utils/metricHours';
import { getRegularHoursForMonth, isSunday } from '../utils/monthUtils';
import '../css/Sati.css';
import '../App.css';

const DAY_NAMES_EXPORT = ['PON', 'UTO', 'SRI', 'ČET', 'PET', 'SUB', 'NED'];
const HOURS_PER_WEEK_EXPORT = 50;

const MONTH_NAMES_LONG = [
  'Siječanj',
  'Veljača',
  'Ožujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
];

/** Isto kao MjesecniSati – zbroj sati iz metrike */
function getHoursFromMetricRow(m) {
  return getHoursFromMetric(m);
}

function makeDateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getFirstNameWord(fullName) {
  const normalized = String(fullName || '').trim();
  if (!normalized) return '';
  return normalized.split(/\s+/)[0] || '';
}

function colToLetter(colIndex) {
  // Excel: 1-based column index -> letter (1 -> A, 28 -> AB, ...)
  let n = colIndex;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters || 'A';
}

/** Ukupno / redovni kapacitet / nedjelja / praznici / prekovremeno za kalendarski mjesec */
function computePersonMonthSummary(personId, year, month, dailyMetricsData, holidaysByDate = {}) {
  const personMetrics = dailyMetricsData.filter(
    (d) => d.userId === personId && d.year === year && d.month === month
  );
  let totalHours = 0;
  let sundayHours = 0;
  let holidayHours = 0;
  let godisnjiHours = 0;
  personMetrics.forEach((d) => {
    const godisnji = parseGodisnjiHours(d);
    if (godisnji > 0) {
      godisnjiHours += godisnji;
      totalHours += godisnji;
      return;
    }
    const workH = getWorkHoursFromMetric(d);
    totalHours += workH;
    if (isSunday(year, month, d.day)) sundayHours += workH;
    if (holidaysByDate[makeDateKey(year, month, d.day)]) holidayHours += workH;
  });
  const regularHours = getRegularHoursForMonth(year, month);
  const overtimeHours = Math.max(0, totalHours - regularHours);
  return {
    total: totalHours,
    regular: regularHours,
    sunday: sundayHours,
    holiday: holidayHours,
    godisnji: godisnjiHours,
    overtime: overtimeHours,
  };
}

const MONTH_NAMES_FILE = [
  'Sijecanj',
  'Veljaca',
  'Ozujak',
  'Travanj',
  'Svibanj',
  'Lipanj',
  'Srpanj',
  'Kolovoz',
  'Rujan',
  'Listopad',
  'Studeni',
  'Prosinac',
];

/** Jedan dokument u `appSettings` (Firestore pravila: pisanje samo admin). */
const OBRACUN_RATES_DOC_ID = 'satiObracunRates';
const OBRACUN_RATE_KEYS = ['sunday', 'night', 'holiday', 'overtime', 'godisnji'];
const OBRACUN_RATE_EPS = 1e-9;

const DEFAULT_OBRACUN_RATES = Object.freeze({
  sunday: 0,
  night: 0,
  holiday: 0,
  overtime: 0,
  godisnji: 0,
  overtimeSplitFirma: 0,
});

function normalizeObracunSplitPercent(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function normalizeObracunRateStored(v) {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
  return n;
}

function obracunRatesFromFirestoreData(data) {
  if (!data) return { ...DEFAULT_OBRACUN_RATES };
  return {
    sunday: normalizeObracunRateStored(data.sunday),
    night: normalizeObracunRateStored(data.night),
    holiday: normalizeObracunRateStored(data.holiday),
    overtime: normalizeObracunRateStored(data.overtime),
    godisnji: normalizeObracunRateStored(data.godisnji),
    overtimeSplitFirma: normalizeObracunSplitPercent(data.overtimeSplitFirma),
  };
}

function obracunRateFieldsEqual(a, b) {
  return Math.abs((a ?? 0) - (b ?? 0)) < OBRACUN_RATE_EPS;
}

function formatObracunDraftValue(n) {
  if (!Number.isFinite(n)) return '0';
  return String(n);
}

/** Jedan list = jedan tjedan (isti format kao pojedinačni izvoz). */
function addSatiWeekWorksheet(workbook, week, filteredPeople, dailyMetricsData) {
  const weekLabel = `${week.label} (${week.dates[0]?.day}.${week.dates[0]?.month}.–${week.dates[6]?.day}.${week.dates[6]?.month}.)`;
  let sheetName = `${week.label} ${week.dates[0]?.day}.${week.dates[0]?.month}`;
  sheetName = sheetName.replace(/[:\\/?*[\]]/g, '-').substring(0, 31);
  const sheet = workbook.addWorksheet(sheetName || 'Tjedan');

  sheet.addRow([`Tjedan: ${weekLabel}`]);
  sheet.addRow([]);
  sheet.addRow(['IME', 'Dan', 'Datum', 'Glovo', 'Wolt', 'Ukupno']);
  sheet.getRow(3).font = { bold: true };

  const getMetricForDate = (userId, year, month, day) => {
    const m = dailyMetricsData.find(
      (d) => d.userId === userId && d.year === year && d.month === month && d.day === day
    );
    if (!m) return { glovo: 0, wolt: 0, total: 0, isGodisnji: false };
    if (isGodisnjiMetric(m)) {
      const total = getHoursFromMetric(m);
      return { glovo: 0, wolt: 0, total, isGodisnji: true };
    }
    const glovo = parseGlovoHours(m);
    const wolt = parseWoltHours(m);
    return { glovo, wolt, total: glovo + wolt, isGodisnji: false };
  };

  filteredPeople.forEach((person) => {
    let weekTotal = 0;
    DAY_NAMES_EXPORT.forEach((dayName, dayIdx) => {
      const dateInfo = week.dates[dayIdx];
      if (!dateInfo) return;
      const metricRow = getMetricForDate(
        person.id,
        dateInfo.year,
        dateInfo.month,
        dateInfo.day
      );
      weekTotal += metricRow.total;
      const datum = `${dateInfo.day}.${dateInfo.month}.${dateInfo.year}.`;
      sheet.addRow([
        person.name,
        dayName,
        datum,
        metricRow.isGodisnji
          ? `Godišnji ${formatDecimalHoursSigned(metricRow.total)}`
          : formatDecimalHoursSigned(metricRow.glovo),
        metricRow.isGodisnji ? '–' : formatDecimalHoursSigned(metricRow.wolt),
        formatDecimalHoursSigned(metricRow.total),
      ]);
    });
    sheet.addRow([
      person.name,
      'Preostalo sati',
      '–',
      '–',
      '–',
      formatDecimalHoursSigned(HOURS_PER_WEEK_EXPORT - weekTotal),
    ]);
    sheet.addRow([]);
  });

  sheet.columns.forEach((col, i) => {
    col.width = i === 0 ? 22 : i === 1 ? 14 : 12;
  });
}

/**
 * Jedan list: osobe jedna ispod druge; stupci = tjedni (Glovo, Wolt, Ukupno), zatim 6 stupaca mjesečnog sažetka.
 * Stupac A: ime; zadnjih 6: ukupno / redovni / nedjelja / noćni (ručno) / praznici / prekovremeno (spojeno preko 8 redaka po osobi).
 */
function addSatiMonthWideWorksheet(
  workbook,
  weekIndices,
  weeks,
  filteredPeople,
  dailyMetricsData,
  year,
  month,
  nightHoursByUserMonth = {},
  holidaysByDate = {},
  obracunRates = DEFAULT_OBRACUN_RATES
) {
  const sheet = workbook.addWorksheet('Sati mjesec');
  const monthName = MONTH_NAMES_LONG[month - 1];
  const W = weekIndices.length;
  const SUMMARY_COLS = 6;
  const OBRACUN_COLS = 5; // STAVKA, UKUPNO H, UKUPNO €, RADNIK €, FIRMA €
  const weekColCount = W * 3;
  const totalCols = 2 + weekColCount + SUMMARY_COLS + OBRACUN_COLS;
  const summaryColStart = 2 + weekColCount + 1; /* 1-based */
  const obracunColStart = summaryColStart + SUMMARY_COLS;
  const sideColStart = totalCols + 2; // right table label
  const sideValueCol = totalCols + 3; // right table value
  const rateSundayCell = `${colToLetter(sideValueCol)}3`;
  const rateNightCell = `${colToLetter(sideValueCol)}4`;
  const rateHolidayCell = `${colToLetter(sideValueCol)}5`;
  const rateGodisnjiCell = `${colToLetter(sideValueCol)}6`;
  const rateOvertimeCell = `${colToLetter(sideValueCol)}7`;
  const overtimeSplitFirmaCell = `${colToLetter(sideValueCol)}8`;

  const getMetricForDate = (userId, y, m, d) => {
    const met = dailyMetricsData.find(
      (x) => x.userId === userId && x.year === y && x.month === m && x.day === d
    );
    if (!met) return { glovo: 0, wolt: 0, total: 0 };
    const glovo = parseFloat(met.glovoHours ?? met.actualWorkingHours ?? 0) || 0;
    const wolt = parseFloat(met.woltHours ?? met.plannedWorkingHours ?? 0) || 0;
    return { glovo, wolt, total: glovo + wolt };
  };

  const titleRow = [`${monthName} ${year}. – svi tjedni (jedan blok po osobi)`];
  for (let i = titleRow.length; i < totalCols; i++) titleRow.push('');
  sheet.addRow(titleRow);
  sheet.mergeCells(1, 1, 1, totalCols);
  sheet.getRow(1).font = { bold: true, size: 12 };

  const row2 = ['', ''];
  weekIndices.forEach((wi) => {
    const w = weeks[wi];
    const lbl = `${w.label} (${w.dates[0]?.day}.${w.dates[0]?.month}.–${w.dates[6]?.day}.${w.dates[6]?.month}.)`;
    row2.push(lbl, '', '');
  });
  row2.push('Mjesečni sati', '', '', '', '', '', 'Obračun', '', '', '', '');
  sheet.addRow(row2);
  for (let w = 0; w < W; w++) {
    const c0 = 3 + w * 3;
    sheet.mergeCells(2, c0, 2, c0 + 2);
  }
  sheet.mergeCells(2, summaryColStart, 2, summaryColStart + SUMMARY_COLS - 1);
  sheet.mergeCells(2, obracunColStart, 2, obracunColStart + OBRACUN_COLS - 1);
  sheet.getCell(2, summaryColStart).value = 'Mjesečni sati (kalendarski mjesec)';
  sheet.getCell(2, obracunColStart).value = 'Obračun (EUR)';
  sheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(2).font = { bold: true, size: 10 };

  const row3 = ['Osoba', 'Dan'];
  weekIndices.forEach(() => {
    row3.push('Glovo', 'Wolt', 'Ukupno');
  });
  row3.push(
    'Ukupno sati',
    'Redovni sati mj.',
    'Nedjelja',
    'Noćni sati',
    'Praznici sati',
    'Prekovremeno',
    '',
    '',
    '',
    '',
    ''
  );
  sheet.addRow(row3);
  sheet.getRow(3).font = { bold: true };
  const obracunHeaderCols = ['STAVKA', 'UKUPNO H', 'UKUPNO €', 'RADNIK €', 'FIRMA €'];
  obracunHeaderCols.forEach((label, i) => {
    const cell = sheet.getCell(3, obracunColStart + i);
    cell.value = label;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.font = { bold: true };
  });

  let currentRow = 4;
  /** Retci „Ukupno EURA” po osobi (za zbroj na dnu lista). */
  const personUkupnoEuraRows = [];

  filteredPeople.forEach((person) => {
    const personStartRow = currentRow;

    for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
      const line = [dayIdx === 0 ? person.name : '', DAY_NAMES_EXPORT[dayIdx]];
      weekIndices.forEach((wi) => {
        const wk = weeks[wi];
        const dateInfo = wk.dates[dayIdx];
        const { glovo, wolt, total } = getMetricForDate(
          person.id,
          dateInfo.year,
          dateInfo.month,
          dateInfo.day
        );
        line.push(
          formatDecimalHoursSigned(glovo),
          formatDecimalHoursSigned(wolt),
          formatDecimalHoursSigned(total)
        );
      });
      for (let s = 0; s < SUMMARY_COLS; s++) line.push('');
      for (let s = 0; s < OBRACUN_COLS; s++) line.push('');
      sheet.addRow(line);
      currentRow++;
    }

    const preLine = ['', 'Preostalo'];
    weekIndices.forEach((wi) => {
      const wk = weeks[wi];
      let weekTotal = 0;
      for (let d = 0; d < 7; d++) {
        const dateInfo = wk.dates[d];
        weekTotal += getMetricForDate(
          person.id,
          dateInfo.year,
          dateInfo.month,
          dateInfo.day
        ).total;
      }
      preLine.push('–', '–', formatDecimalHoursSigned(HOURS_PER_WEEK_EXPORT - weekTotal));
    });
    for (let s = 0; s < SUMMARY_COLS; s++) preLine.push('');
    for (let s = 0; s < OBRACUN_COLS; s++) preLine.push('');
    sheet.addRow(preLine);
    currentRow++;
    const nightH = nightHoursByUserMonth[`${person.id}_${month}`] ?? 0;
    const summary = computePersonMonthSummary(
      person.id,
      year,
      month,
      dailyMetricsData,
      holidaysByDate
    );

    // 6 redaka unutar "Obračun" bloka (PON–SUB; NED i Preostalo prazni)
    const obracunRows = {
      sunday: personStartRow,
      night: personStartRow + 1,
      holiday: personStartRow + 2,
      godisnji: personStartRow + 3,
      overtime: personStartRow + 4,
      total: personStartRow + 5,
    };
    const personEndRow = currentRow - 1; // završava na "Preostalo"

    // Summary kolone (UKUPNO... PREKOVREMENO) spajamo preko cijelog bloka osobe
    const summaryVals = [
      formatDecimalHoursSigned(summary.total),
      formatDecimalHoursSigned(summary.regular),
      formatDecimalHoursSigned(summary.sunday),
      formatDecimalHoursSigned(nightH),
      formatDecimalHoursSigned(summary.holiday),
      formatDecimalHoursSigned(summary.overtime),
    ];
    for (let i = 0; i < SUMMARY_COLS; i++) {
      const col = summaryColStart + i;
      const cell = sheet.getCell(personStartRow, col);
      cell.value = summaryVals[i];
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.font = { bold: true };
      sheet.mergeCells(personStartRow, col, personEndRow, col);
    }

    // Ime spajamo preko cijelog bloka osobe
    sheet.mergeCells(personStartRow, 1, personEndRow, 1);
    const nameCell = sheet.getCell(personStartRow, 1);
    nameCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    nameCell.font = { bold: true };

    const labelCol = obracunColStart;
    const hoursCol = obracunColStart + 1;
    const eurCol = obracunColStart + 2;
    const radnikCol = obracunColStart + 3;
    const firmaCol = obracunColStart + 4;
    const eurL = colToLetter(eurCol);
    const radnikL = colToLetter(radnikCol);
    const firmaL = colToLetter(firmaCol);
    const hoursL = colToLetter(hoursCol);

    const fillObracunStandardRow = (rowNum, label, hours, rateCell) => {
      sheet.getCell(rowNum, labelCol).value = label;
      sheet.getCell(rowNum, hoursCol).value = hours;
      sheet.getCell(rowNum, hoursCol).numFmt = '0.00';
      sheet.getCell(rowNum, eurCol).value = {
        formula: `=${hoursL}${rowNum}*${rateCell}`,
      };
      sheet.getCell(rowNum, eurCol).numFmt = '0.00';
      sheet.getCell(rowNum, radnikCol).value = { formula: `=${eurL}${rowNum}` };
      sheet.getCell(rowNum, radnikCol).numFmt = '0.00';
      sheet.getCell(rowNum, firmaCol).value = 0;
      sheet.getCell(rowNum, firmaCol).numFmt = '0.00';
    };

    // Popuni side table vrijednosti (user ih može mijenjati u Excelu)
    sheet.getCell(2, sideColStart).value = 'Tablica sastrane – stope / split';
    sheet.getCell(2, sideColStart).font = { bold: true };

    const sideLabels = [
      [3, 'NEDELJA'],
      [4, 'NOCNI SATI'],
      [5, 'PRAZNICI SATI'],
      [6, 'GODIŠNJI SATI'],
      [7, 'PREKOVREMENO SATI'],
      [8, 'PREKOVREMENO SPLIT (firma %)'],
    ];
    sideLabels.forEach(([row, text]) => {
      sheet.getCell(row, sideColStart).value = text;
    });

    [
      [3, obracunRates.sunday],
      [4, obracunRates.night],
      [5, obracunRates.holiday],
      [6, obracunRates.godisnji],
      [7, obracunRates.overtime],
      [8, obracunRates.overtimeSplitFirma],
    ].forEach(([row, val]) => {
      const c = sheet.getCell(row, sideValueCol);
      c.value = typeof val === 'number' ? val : Number(val) || 0;
      c.numFmt = row === 8 ? '0.00' : '0.00';
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.font = { bold: true };
    });

    fillObracunStandardRow(obracunRows.sunday, 'Nedjelja', summary.sunday, rateSundayCell);
    fillObracunStandardRow(obracunRows.night, 'Noćni sati', nightH, rateNightCell);
    fillObracunStandardRow(obracunRows.holiday, 'Praznici sati', summary.holiday, rateHolidayCell);
    fillObracunStandardRow(
      obracunRows.godisnji,
      'Godišnji sati',
      summary.godisnji ?? 0,
      rateGodisnjiCell
    );

    const otRow = obracunRows.overtime;
    sheet.getCell(otRow, labelCol).value = 'Prekovremeno sati';
    sheet.getCell(otRow, hoursCol).value = summary.overtime;
    sheet.getCell(otRow, hoursCol).numFmt = '0.00';
    sheet.getCell(otRow, eurCol).value = {
      formula: `=${hoursL}${otRow}*${rateOvertimeCell}`,
    };
    sheet.getCell(otRow, eurCol).numFmt = '0.00';
    sheet.getCell(otRow, firmaCol).value = {
      formula: `=${eurL}${otRow}*${overtimeSplitFirmaCell}/100`,
    };
    sheet.getCell(otRow, firmaCol).numFmt = '0.00';
    sheet.getCell(otRow, radnikCol).value = {
      formula: `=${eurL}${otRow}-${firmaL}${otRow}`,
    };
    sheet.getCell(otRow, radnikCol).numFmt = '0.00';

    const totRow = obracunRows.total;
    sheet.getCell(totRow, labelCol).value = 'Ukupno EURA';
    sheet.getCell(totRow, hoursCol).value = {
      formula: `SUM(${hoursL}${obracunRows.sunday}:${hoursL}${obracunRows.overtime})`,
    };
    sheet.getCell(totRow, hoursCol).numFmt = '0.00';
    sheet.getCell(totRow, eurCol).value = {
      formula: `SUM(${eurL}${obracunRows.sunday}:${eurL}${obracunRows.overtime})`,
    };
    sheet.getCell(totRow, eurCol).numFmt = '0.00';
    sheet.getCell(totRow, radnikCol).value = {
      formula: `SUM(${radnikL}${obracunRows.sunday}:${radnikL}${obracunRows.overtime})`,
    };
    sheet.getCell(totRow, radnikCol).numFmt = '0.00';
    sheet.getCell(totRow, firmaCol).value = {
      formula: `SUM(${firmaL}${obracunRows.sunday}:${firmaL}${obracunRows.overtime})`,
    };
    sheet.getCell(totRow, firmaCol).numFmt = '0.00';
    personUkupnoEuraRows.push(totRow);

    const obracunRowNums = [
      obracunRows.sunday,
      obracunRows.night,
      obracunRows.holiday,
      obracunRows.godisnji,
      obracunRows.overtime,
      obracunRows.total,
    ];
    obracunRowNums.forEach((rr) => {
      for (let c = labelCol; c <= firmaCol; c++) {
        const cell = sheet.getCell(rr, c);
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        if (c === labelCol || c === eurCol || c === radnikCol || c === firmaCol) {
          cell.font = { ...(cell.font || {}), bold: true };
        }
      }
    });

    // Border rectangle oko cijelog bloka osobe
    const lastMainCol = totalCols;
    const thinBorder = { style: 'thin', color: { argb: 'FF000000' } };
    for (let col = 1; col <= lastMainCol; col++) {
      sheet.getCell(personStartRow, col).border = {
        ...(sheet.getCell(personStartRow, col).border || {}),
        top: thinBorder,
      };
      sheet.getCell(personEndRow, col).border = {
        ...(sheet.getCell(personEndRow, col).border || {}),
        bottom: thinBorder,
      };
    }
    for (let row = personStartRow; row <= personEndRow; row++) {
      sheet.getCell(row, 1).border = {
        ...(sheet.getCell(row, 1).border || {}),
        left: thinBorder,
      };
      sheet.getCell(row, lastMainCol).border = {
        ...(sheet.getCell(row, lastMainCol).border || {}),
        right: thinBorder,
      };
    }

    // Razmak između osoba
    sheet.addRow([]);
    currentRow++;
  });

  if (personUkupnoEuraRows.length > 0) {
    const grandLabelCol = obracunColStart;
    const grandHoursCol = obracunColStart + 1;
    const grandEurCol = obracunColStart + 2;
    const grandRadnikCol = obracunColStart + 3;
    const grandFirmaCol = obracunColStart + 4;
    const grandHoursL = colToLetter(grandHoursCol);
    const grandEurL = colToLetter(grandEurCol);
    const grandRadnikL = colToLetter(grandRadnikCol);
    const grandFirmaL = colToLetter(grandFirmaCol);
    const sumAcrossPeople = (colL) =>
      personUkupnoEuraRows.map((r) => `${colL}${r}`).join(',');

    const grandLine = new Array(totalCols).fill('');
    grandLine[0] = 'UKUPNO SVIH OSOBA';
    const grandRowNum = sheet.addRow(grandLine).number;

    sheet.getCell(grandRowNum, grandLabelCol).value = 'UKUPNO';
    sheet.getCell(grandRowNum, grandHoursCol).value = {
      formula: `SUM(${sumAcrossPeople(grandHoursL)})`,
    };
    sheet.getCell(grandRowNum, grandHoursCol).numFmt = '0.00';
    sheet.getCell(grandRowNum, grandEurCol).value = {
      formula: `SUM(${sumAcrossPeople(grandEurL)})`,
    };
    sheet.getCell(grandRowNum, grandEurCol).numFmt = '0.00';
    sheet.getCell(grandRowNum, grandRadnikCol).value = {
      formula: `SUM(${sumAcrossPeople(grandRadnikL)})`,
    };
    sheet.getCell(grandRowNum, grandRadnikCol).numFmt = '0.00';
    sheet.getCell(grandRowNum, grandFirmaCol).value = {
      formula: `SUM(${sumAcrossPeople(grandFirmaL)})`,
    };
    sheet.getCell(grandRowNum, grandFirmaCol).numFmt = '0.00';

    for (let c = grandLabelCol; c <= grandFirmaCol; c++) {
      const cell = sheet.getCell(grandRowNum, c);
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE2E8F0' },
      };
    }
    const nameGrandCell = sheet.getCell(grandRowNum, 1);
    nameGrandCell.font = { bold: true };
    nameGrandCell.alignment = { vertical: 'middle', horizontal: 'left' };

    currentRow = grandRowNum + 1;
  }

  // Vertikalni obrubi po tjednima (lakše vizualno čitanje)
  const lastUsedRow = Math.max(3, currentRow - 1);
  const weekBorder = { style: 'thin', color: { argb: 'FF9CA3AF' } };
  for (let w = 0; w < W; w++) {
    const weekStartCol = 3 + w * 3; // Glovo
    const weekEndCol = weekStartCol + 2; // Ukupno
    for (let r = 2; r <= lastUsedRow; r++) {
      sheet.getCell(r, weekStartCol).border = {
        ...(sheet.getCell(r, weekStartCol).border || {}),
        left: weekBorder,
      };
      sheet.getCell(r, weekEndCol).border = {
        ...(sheet.getCell(r, weekEndCol).border || {}),
        right: weekBorder,
      };
    }
  }

  sheet.getColumn(1).width = 26;
  sheet.getColumn(2).width = 12;
  for (let c = 3; c < summaryColStart; c++) {
    sheet.getColumn(c).width = 11;
  }
  for (let c = summaryColStart; c < summaryColStart + SUMMARY_COLS; c++) {
    sheet.getColumn(c).width = 14;
  }
  for (let c = obracunColStart; c < obracunColStart + OBRACUN_COLS; c++) {
    sheet.getColumn(c).width = c === obracunColStart ? 16 : 12;
  }

  // Auto širina SAMO za tablicu sastrane (da glavni stupci ostanu stabilni)
  [sideColStart, sideValueCol].forEach((colIdx) => {
    const column = sheet.getColumn(colIdx);
    let maxLength = 10;
    column.eachCell({ includeEmpty: false }, (cell) => {
      const txt = String(cell.value ?? '');
      maxLength = Math.max(maxLength, txt.length + 2);
    });
    column.width = Math.min(40, maxLength);
  });
}

Modal.setAppElement('#root');

const OBRACUN_ALL_KEYS = [...OBRACUN_RATE_KEYS, 'overtimeSplitFirma'];

function ObracunSettingsModal({ isOpen, onClose, baselineRates, onSave }) {
  const [draft, setDraft] = useState({
    sunday: '',
    night: '',
    holiday: '',
    overtime: '',
    godisnji: '',
    overtimeSplitFirma: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !baselineRates) return;
    setDraft({
      sunday: formatObracunDraftValue(baselineRates.sunday),
      night: formatObracunDraftValue(baselineRates.night),
      holiday: formatObracunDraftValue(baselineRates.holiday),
      overtime: formatObracunDraftValue(baselineRates.overtime),
      godisnji: formatObracunDraftValue(baselineRates.godisnji),
      overtimeSplitFirma: formatObracunDraftValue(baselineRates.overtimeSplitFirma),
    });
  }, [isOpen, baselineRates]);

  const parseDraftToFull = () => {
    const full = {};
    for (const key of OBRACUN_ALL_KEYS) {
      const raw = String(draft[key] ?? '').trim().replace(',', '.');
      const base = baselineRates?.[key] ?? 0;
      if (raw === '') {
        full[key] = base;
        continue;
      }
      const parsed = parseFloat(raw);
      if (!Number.isFinite(parsed)) {
        return {
          ok: false,
          error: 'Jedan od unosa nije valjan broj. Koristi točku ili zarez za decimale.',
          full: null,
        };
      }
      if (key === 'overtimeSplitFirma') {
        if (parsed < 0 || parsed > 100) {
          return {
            ok: false,
            error: 'Prekovremeno split (firma) mora biti između 0 i 100 (%).',
            full: null,
          };
        }
        full[key] = parsed;
        continue;
      }
      if (parsed < 0) {
        return { ok: false, error: 'Stope moraju biti ≥ 0.', full: null };
      }
      full[key] = parsed;
    }
    return { ok: true, error: '', full };
  };

  const handleSave = async () => {
    if (!baselineRates) return;
    const { ok, error, full } = parseDraftToFull();
    if (!ok) {
      alert(error);
      return;
    }
    const patch = {};
    OBRACUN_ALL_KEYS.forEach((k) => {
      if (!obracunRateFieldsEqual(full[k], baselineRates[k])) patch[k] = full[k];
    });
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(full);
      onClose();
    } catch {
      /* grešku prikazuje roditelj */
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={isSaving ? () => {} : onClose}
      contentLabel="Obračun postavke"
      className="modal"
      overlayClassName="overlay"
    >
      <div className="modal-header">
        <h2>Obračun postavke</h2>
        <button type="button" onClick={onClose} className="close-modal-button" disabled={isSaving}>
          ×
        </button>
      </div>

      <div className="obracun-settings-form">
        <label>
          NEDELJA (EUR/sat)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.sunday}
            onChange={(e) => setDraft((prev) => ({ ...prev, sunday: e.target.value }))}
            disabled={isSaving}
          />
        </label>
        <label>
          NOCNI SATI (EUR/sat)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.night}
            onChange={(e) => setDraft((prev) => ({ ...prev, night: e.target.value }))}
            disabled={isSaving}
          />
        </label>
        <label>
          PRAZNICI SATI (EUR/sat)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.holiday}
            onChange={(e) => setDraft((prev) => ({ ...prev, holiday: e.target.value }))}
            disabled={isSaving}
          />
        </label>
        <label>
          PREKOVREMENO SATI (EUR/sat)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.overtime}
            onChange={(e) => setDraft((prev) => ({ ...prev, overtime: e.target.value }))}
            disabled={isSaving}
          />
        </label>
        <label>
          GODIŠNJI SATI (EUR/sat)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.godisnji}
            onChange={(e) => setDraft((prev) => ({ ...prev, godisnji: e.target.value }))}
            disabled={isSaving}
          />
        </label>
        <label>
          PREKOVREMENO SPLIT – udjelo firme (%)
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draft.overtimeSplitFirma}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, overtimeSplitFirma: e.target.value }))
            }
            disabled={isSaving}
          />
        </label>
        <p className="obracun-split-hint">
          Primjer: 30 znači da firma dobiva 30% iznosa prekovremenih sati, radnik 70%.
        </p>

        <div className="button-group">
          <button type="button" className="save-button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Spremanje…' : 'Spremi'}
          </button>
          <button type="button" className="cancel-button" onClick={onClose} disabled={isSaving}>
            Odustani
          </button>
        </div>
      </div>
    </Modal>
  );
}

function filterUploadedDaysForSearch(days, firms, rawQuery) {
  const sorted = [...days].sort((a, b) => {
    const dateA = new Date(a.year, a.month - 1, a.day);
    const dateB = new Date(b.year, b.month - 1, b.day);
    return dateB - dateA;
  });
  const raw = rawQuery.trim().toLowerCase();
  if (!raw) return sorted;
  const tokens = raw.split(/\s+/).filter(Boolean);
  return sorted.filter((d) => {
    const firmName = firms.find((f) => f.id === d.firmId)?.name ?? 'Default';
    const platformLabel = d.platform === 'glovo' ? 'Glovo' : 'Wolt';
    const { day, month, year } = d;
    const dateDot = `${day}.${month}.${year}`;
    const dateDotPadded = `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
    const dateIso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const hay = [
      dateDot,
      dateDotPadded,
      dateIso,
      String(day),
      String(month),
      String(year),
      firmName,
      String(d.firmId || ''),
      String(d.platform || ''),
      platformLabel,
      String(d.filename || ''),
    ]
      .join(' ')
      .toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
}

/** Lokalno stanje pretrage + useDeferredValue: roditelj (Sati) se ne re-renderira pri tipkanju. */
function UploadedDaysModal({ isOpen, onClose, uploadedDaysHours, firms, onDeleteUploadedDay }) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!isOpen) return [];
    return filterUploadedDaysForSearch(uploadedDaysHours, firms, deferredQuery);
  }, [isOpen, uploadedDaysHours, firms, deferredQuery]);

  const getFirmName = (firmId) => {
    const firm = firms.find((f) => f.id === firmId);
    return firm ? firm.name : 'Default';
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Učitani dani"
      className="modal uploaded-weeks-modal"
      overlayClassName="overlay"
    >
      <div className="modal-header">
        <h2>Učitani dani (Glovo / Wolt)</h2>
        <button type="button" onClick={onClose} className="close-modal-button">
          ×
        </button>
      </div>
      {uploadedDaysHours.length === 0 ? (
        <p className="no-data">Nema učitanih dana.</p>
      ) : isOpen ? (
        <>
          <div className="uploaded-days-modal-toolbar">
            <input
              type="search"
              className="uploaded-days-search-input"
              placeholder="Pretraži po datumu, firmi, platformi, nazivu datoteke…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Pretraži učitane dane"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="no-data">Nema rezultata za traženi upit.</p>
          ) : (
            <ul className="uploaded-days-list">
              {filtered.map((d) => (
                <li key={d.id} className="uploaded-day-item">
                  <span className="day-info">
                    {d.day}.{d.month}.{d.year} – {d.platform === 'glovo' ? 'Glovo' : 'Wolt'}
                    {d.firmId && ` (${getFirmName(d.firmId)})`}
                  </span>
                  <span className="day-filename">{d.filename}</span>
                  <button
                    type="button"
                    className="delete-week-button"
                    onClick={() =>
                      onDeleteUploadedDay(d.day, d.month, d.year, d.platform, d.firmId, d.id)
                    }
                  >
                    Izbriši
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </Modal>
  );
}

function Sati() {
  const { currentUser } = useContext(AuthContext);
  const { showLoading, hideLoading } = useLoading();
  const [people, setPeople] = useState([]);
  const [firms, setFirms] = useState([]);
  const [dailyMetricsData, setDailyMetricsData] = useState([]);
  /** `yyyy-mm-dd` -> naziv praznika */
  const [holidaysByDate, setHolidaysByDate] = useState({});
  /** `${userId}_${month}` -> noćni sati za selectedYear (ručno, Firestore) */
  const [monthlyNightHoursMap, setMonthlyNightHoursMap] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedFirm, setSelectedFirm] = useState('');

  const [isObracunSettingsOpen, setIsObracunSettingsOpen] = useState(false);
  const [obracunModalBaseline, setObracunModalBaseline] = useState(null);
  const [obracunRates, setObracunRates] = useState(() => ({ ...DEFAULT_OBRACUN_RATES }));

  const [isFirmManagerOpen, setIsFirmManagerOpen] = useState(false);
  const [isArchivedPersonsModalOpen, setIsArchivedPersonsModalOpen] = useState(false);
  const [isPersonFormOpen, setIsPersonFormOpen] = useState(false);
  const [isUploadedDaysModalOpen, setIsUploadedDaysModalOpen] = useState(false);
  const [uploadedDaysHours, setUploadedDaysHours] = useState([]);
  const [uploadedDaysLoaded, setUploadedDaysLoaded] = useState(false);
  const [showHoursTable, setShowHoursTable] = useState(false);
  const staticDataLoadedRef = useRef(false);
  const pageLoadIdRef = useRef(0);
  const weeks = useMemo(() => getWeeksInYear(selectedYear), [selectedYear]);
  const [selectedExportWeek, setSelectedExportWeek] = useState(0);
  /** Mjesec za izvoz svih tjedana odjednom (1–12) */
  const [exportMonthAllWeeks, setExportMonthAllWeeks] = useState(() => new Date().getMonth() + 1);

  useEffect(() => {
    setSelectedExportWeek(getCurrentWeekIndex(selectedYear));
  }, [selectedYear]);

  const loadFirmsData = async () => {
    const firmsCollection = collection(db, 'firms');
    const querySnapshot = await getDocs(firmsCollection);
    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  };

  const fetchFirms = async () => {
    setFirms(await loadFirmsData());
  };

  const ensureDefaultFirm = async () => {
    const defaultFirmRef = doc(db, 'firms', 'default');
    const defaultFirmSnap = await getDoc(defaultFirmRef);
    if (!defaultFirmSnap.exists()) {
      await setDoc(defaultFirmRef, {
        name: 'Default',
        createdAt: new Date(),
      });
    }
  };

  const loadMonthlyNightHoursData = async (year) => {
    const q = query(collection(db, 'monthlyNightHours'), where('year', '==', year));
    const snapshot = await getDocs(q);
    const map = {};
    snapshot.docs.forEach((d) => {
      const data = d.data();
      const uid = data.userId;
      const m = data.month;
      if (uid != null && m >= 1 && m <= 12) {
        map[`${uid}_${m}`] = data.hours ?? 0;
      }
    });
    return map;
  };

  const fetchMonthlyNightHours = async () => {
    try {
      setMonthlyNightHoursMap(await loadMonthlyNightHoursData(selectedYear));
    } catch (error) {
      console.error('Greška pri dohvaćanju noćnih sati:', error);
    }
  };

  const loadHolidaysData = async (year) => {
    const q = query(collection(db, 'holidays'), where('year', '==', year));
    const snapshot = await getDocs(q);
    const map = {};
    snapshot.docs.forEach((d) => {
      const data = d.data();
      const key =
        data.dateKey ||
        `${data.year}-${String(data.month).padStart(2, '0')}-${String(data.day).padStart(2, '0')}`;
      if (key) map[key] = data.name || 'Praznik';
    });
    return map;
  };

  const fetchHolidays = async () => {
    try {
      setHolidaysByDate(await loadHolidaysData(selectedYear));
    } catch (error) {
      console.error('Greška pri dohvaćanju praznika:', error);
    }
  };

  const loadUsersData = async () => {
    const qUsers = query(collection(db, 'users'));
    const querySnapshot = await getDocs(qUsers);
    return querySnapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name || '',
        email: data.email || '',
        lozinka: data.lozinka || '',
        city: data.city || '',
        autogeneratedId: data.autogeneratedId ?? null,
        firmId: data.firmId || 'default',
        glovoId: data.glovoId || '',
        woltId: data.woltId || '',
        startMonth: data.startMonth ?? new Date().getMonth() + 1,
        startYear: data.startYear ?? new Date().getFullYear(),
        archived: data.archived || false,
        archiveMonth: data.archiveMonth || null,
        archiveYear: data.archiveYear || null,
        authId: data.authId || null,
        authUid: data.authUid || null,
      };
    });
  };

  const fetchUsers = async () => {
    setPeople(await loadUsersData());
  };

  const fetchDailyMetrics = async () => {
    const metrics = await fetchDailyMetricsForCalendarYear(db, selectedYear);
    setDailyMetricsData(metrics);
  };

  const fetchObracunRates = async () => {
    try {
      const ref = doc(db, 'appSettings', OBRACUN_RATES_DOC_ID);
      const snap = await getDoc(ref);
      if (!snap.exists()) return;
      setObracunRates(obracunRatesFromFirestoreData(snap.data()));
    } catch (error) {
      console.error('Greška pri dohvaćanju obračunskih postavki:', error);
    }
  };

  const openObracunSettings = () => {
    setObracunModalBaseline({ ...obracunRates });
    setIsObracunSettingsOpen(true);
  };

  const handleSaveObracunRates = async (full) => {
    const baseline = obracunModalBaseline;
    if (!baseline) {
      alert('Zatvorite modal i otvorite „Obračun postavke“ ponovno.');
      throw new Error('missing obracun baseline');
    }
    const patch = {};
    OBRACUN_ALL_KEYS.forEach((k) => {
      if (!obracunRateFieldsEqual(full[k], baseline[k])) patch[k] = full[k];
    });
    if (Object.keys(patch).length === 0) return;

    showLoading('Spremanje obračunskih postavki...');
    try {
      await setDoc(
        doc(db, 'appSettings', OBRACUN_RATES_DOC_ID),
        { ...patch, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setObracunRates({ ...full });
    } catch (error) {
      console.error('Greška pri spremanju obračunskih postavki:', error);
      alert(
        'Spremanje nije uspjelo. Provjerite vezu i jeste li prijavljeni kao administrator (zapis u appSettings).'
      );
      throw error;
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    if (!currentUser) {
      staticDataLoadedRef.current = false;
      setShowHoursTable(false);
      return;
    }

    const loadId = ++pageLoadIdRef.current;
    showLoading('Učitavanje sati...');
    setShowHoursTable(false);

    const finishLoading = () => {
      if (loadId === pageLoadIdRef.current) hideLoading();
    };

    const loadPageData = async () => {
      try {
        if (!staticDataLoadedRef.current) {
          await ensureDefaultFirm();
          const [firmsData, users] = await Promise.all([loadFirmsData(), loadUsersData()]);
          if (loadId !== pageLoadIdRef.current) return;
          setFirms(firmsData);
          setPeople(users);
          await fetchObracunRates();
          staticDataLoadedRef.current = true;
        }

        const [metrics, nightMap, holMap] = await Promise.all([
          fetchDailyMetricsForCalendarYear(db, selectedYear),
          loadMonthlyNightHoursData(selectedYear),
          loadHolidaysData(selectedYear),
        ]);
        if (loadId !== pageLoadIdRef.current) return;

        finishLoading();
        startTransition(() => {
          if (loadId !== pageLoadIdRef.current) return;
          setDailyMetricsData(metrics);
          setMonthlyNightHoursMap(nightMap);
          setHolidaysByDate(holMap);
          setShowHoursTable(true);
        });
      } catch (error) {
        console.error('Greška pri učitavanju:', error);
        finishLoading();
      }
    };

    loadPageData();
    return finishLoading;
  }, [currentUser, selectedYear]);

  const getFirmName = (firmId) => {
    const firm = firms.find((f) => f.id === firmId);
    return firm ? firm.name : 'Default';
  };

  const addPerson = async (person) => {
    const selectedFirmObj = firms.find((f) => f.id === person.firmId);
    if (!selectedFirmObj) {
      alert('Odabrana firma ne postoji.');
      return;
    }
    showLoading('Spremanje osobe...');
    try {
      const docRef = await addDoc(collection(db, 'users'), person);
      setPeople((prev) => [...prev, { id: docRef.id, ...person }]);
      alert('Osoba uspješno dodana!');
    } catch (error) {
      console.error('Greška pri dodavanju osobe:', error);
      alert('Došlo je do greške prilikom dodavanja osobe. Pokušajte ponovno.');
    } finally {
      hideLoading();
    }
  };

  const updatePerson = async (updatedPerson) => {
    showLoading('Ažuriranje osobe...');
    try {
      const { id, ...data } = updatedPerson;
      await updateDoc(doc(db, 'users', id), data);
      setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, ...data } : p)));
      alert('Osoba uspješno ažurirana!');
      refreshData();
    } catch (error) {
      console.error('Greška pri ažuriranju osobe:', error);
      alert('Došlo je do greške prilikom ažuriranja.');
    } finally {
      hideLoading();
    }
  };

  const archivePerson = async (person) => {
    showLoading('Arhiviranje...');
    const now = new Date();
    const archiveMonth = now.getMonth() + 1;
    const archiveYear = now.getFullYear();
    try {
      await updateDoc(doc(db, 'users', person.id), {
        archived: true,
        archiveMonth,
        archiveYear,
      });
      setPeople((prev) =>
        prev.map((p) =>
          p.id === person.id ? { ...p, archived: true, archiveMonth, archiveYear } : p
        )
      );
      alert('Osoba uspješno arhivirana.');
      refreshData();
    } catch (error) {
      console.error('Greška pri arhiviranju:', error);
      alert('Došlo je do greške prilikom arhiviranja.');
    } finally {
      hideLoading();
    }
  };

  const unarchivePerson = async (personId) => {
    showLoading('Odarhiviranje...');
    try {
      await updateDoc(doc(db, 'users', personId), {
        archived: false,
        archiveMonth: null,
        archiveYear: null,
      });
      setPeople((prev) =>
        prev.map((p) =>
          p.id === personId ? { ...p, archived: false, archiveMonth: null, archiveYear: null } : p
        )
      );
      alert('Osoba uspješno odarhivirana!');
    } catch (error) {
      console.error('Greška pri odarhiviranju:', error);
      alert('Došlo je do greške prilikom odarhiviranja.');
    } finally {
      hideLoading();
    }
  };

  const fetchUploadedDaysHours = async () => {
    const years = [selectedYear - 1, selectedYear, selectedYear + 1];
    const q = query(collection(db, 'uploadedDaysHours'), where('year', 'in', years));
    const snapshot = await getDocs(q);
    const days = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    setUploadedDaysHours(days);
    setUploadedDaysLoaded(true);
    return days;
  };

  const ensureUploadedDaysHours = async () => {
    if (uploadedDaysLoaded) return;
    showLoading('Učitavanje uvezenih dana...');
    try {
      await fetchUploadedDaysHours();
    } catch (error) {
      console.error('Greška pri dohvaćanju uploadanih dana:', error);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    setUploadedDaysLoaded(false);
    setUploadedDaysHours([]);
  }, [selectedYear]);

  const handleExcelUpload = async (platform, dateInfo, data, filename) => {
    const { year, month, day } = dateInfo;

    if (!selectedFirm) {
      alert('Molimo odaberite firmu u filtru prije upload-a.');
      return;
    }

    let uploadedDays = uploadedDaysHours;
    if (!uploadedDaysLoaded) {
      try {
        uploadedDays = await fetchUploadedDaysHours();
      } catch (error) {
        console.error('Greška pri učitavanju uvezenih dana:', error);
      }
    }

    const uniqueId = `${year}-${month}-${day}-${platform}-${selectedFirm}`;

    const existing = uploadedDays.find(
      (d) => d.day === day && d.month === month && d.year === year && d.platform === platform && d.firmId === selectedFirm
    );
    if (existing) {
      if (!window.confirm(`Dan ${day}.${month}. za ${platform === 'glovo' ? 'Glovo' : 'Wolt'} i firmu ${getFirmName(selectedFirm)} je već uvezen. Prepisati?`)) {
        return;
      }
    }

    const peopleForFirm = people.filter((p) => p.firmId === selectedFirm);
    let results = [];
    if (platform === 'glovo') {
      results = parseGlovoExcel(data, peopleForFirm);
    } else {
      results = parseWoltExcel(data, peopleForFirm);
    }

    if (results.length === 0) {
      alert(`Nije pronađena nijedna osoba u Excelu. Provjerite stupce ${platform === 'glovo' ? 'Rider ID (glovoId) i sum CDT in minutes' : 'Id (woltId) i Total on-task time'}.`);
      return;
    }

    showLoading('Uvoz Excel datoteke...');
    try {
      for (const { userId, glovoHours, woltHours } of results) {
        const personData = people.find((p) => p.id === userId);
        const authUid = personData?.authId || personData?.authUid;
        const qMetric = query(
          collection(db, 'dailyMetrics'),
          where('userId', '==', userId),
          where('year', '==', year),
          where('month', '==', month),
          where('day', '==', day)
        );
        const snap = await getDocs(qMetric);
        const updateData = platform === 'glovo'
          ? { glovoHours: glovoHours ?? 0, godisnjiHours: 0 }
          : { woltHours: woltHours ?? 0, godisnjiHours: 0 };
        if (authUid) updateData.authUid = authUid;

        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, updateData);
        } else {
          const newMetric = {
            userId,
            year,
            month,
            day,
            amount: 0,
            godisnjiHours: 0,
            glovoHours: platform === 'glovo' ? (glovoHours ?? 0) : 0,
            woltHours: platform === 'wolt' ? (woltHours ?? 0) : 0,
          };
          if (authUid) newMetric.authUid = authUid;
          await addDoc(collection(db, 'dailyMetrics'), newMetric);
        }
      }

      await setDoc(doc(db, 'uploadedDaysHours', uniqueId), {
        day,
        month,
        year,
        filename,
        platform,
        firmId: selectedFirm,
      });
      setUploadedDaysHours((prev) => {
        const filtered = prev.filter((d) => d.id !== uniqueId);
        return [...filtered, { id: uniqueId, day, month, year, filename, platform }];
      });
      alert(`Uspješno uvezeno ${results.length} osoba za ${day}.${month}.${year} (${platform === 'glovo' ? 'Glovo' : 'Wolt'}) – firma ${getFirmName(selectedFirm)}.`);
      refreshData();
    } catch (error) {
      console.error('Greška pri uploadu:', error);
      alert('Došlo je do greške prilikom uvoza.');
    } finally {
      hideLoading();
    }
  };

  const deleteUploadedDayHours = async (day, month, year, platform, firmId, docId) => {
    const firmIdToUse = firmId || 'default';
    if (!window.confirm(`Obrisati ${platform === 'glovo' ? 'Glovo' : 'Wolt'} sati za ${day}.${month}.${year}${firmIdToUse ? ` za firmu ${getFirmName(firmIdToUse)}` : ''}?`)) {
      return;
    }

    try {
      const peopleInFirmIds = new Set(people.filter((p) => p.firmId === firmIdToUse).map((p) => p.id));

      const qMetrics = query(
        collection(db, 'dailyMetrics'),
        where('year', '==', year),
        where('month', '==', month),
        where('day', '==', day)
      );
      const metricsSnap = await getDocs(qMetrics);

      const peopleMap = new Map(people.map((p) => [p.id, p]));
      for (const docSnap of metricsSnap.docs) {
        const data = docSnap.data();
        if (peopleInFirmIds.has(data.userId)) {
          const personData = peopleMap.get(data.userId);
          const authUid = personData?.authId || personData?.authUid;
          const updateData = platform === 'glovo'
            ? { glovoHours: 0 }
            : { woltHours: 0 };
          if (authUid) updateData.authUid = authUid;
          await updateDoc(docSnap.ref, updateData);
        }
      }

      const idToDelete = docId || `${year}-${month}-${day}-${platform}-${firmIdToUse}`;
      await deleteDoc(doc(db, 'uploadedDaysHours', idToDelete));
      setUploadedDaysHours((prev) => prev.filter((d) => d.id !== idToDelete));
      alert(`Dan ${day}.${month}.${year} (${platform})${firmIdToUse ? ` za firmu ${getFirmName(firmIdToUse)}` : ''} je obrisan.`);
      refreshData();
    } catch (error) {
      console.error('Greška pri brisanju:', error);
      alert('Došlo je do greške prilikom brisanja.');
    } finally {
      hideLoading();
    }
  };

  const refreshData = useCallback(async () => {
    showLoading('Osvježavanje...');
    setShowHoursTable(false);
    try {
      const tasks = [
        loadFirmsData(),
        loadUsersData(),
        fetchDailyMetricsForCalendarYear(db, selectedYear),
        loadMonthlyNightHoursData(selectedYear),
        loadHolidaysData(selectedYear),
      ];
      if (uploadedDaysLoaded) tasks.push(fetchUploadedDaysHours());
      const results = await Promise.all(tasks);
      const [firmsData, users, metrics, nightMap, holMap] = results;
      hideLoading();
      startTransition(() => {
        setFirms(firmsData);
        setPeople(users);
        setDailyMetricsData(metrics);
        setMonthlyNightHoursMap(nightMap);
        setHolidaysByDate(holMap);
        setShowHoursTable(true);
      });
    } catch (error) {
      console.error('Greška pri osvježavanju:', error);
      hideLoading();
    }
  }, [selectedYear, uploadedDaysLoaded, hideLoading, showLoading]);

  const filteredPeopleForExport = useMemo(() => {
    let filtered = [...people];
    if (selectedFirm) filtered = filtered.filter((p) => p.firmId === selectedFirm);
    filtered = filtered.filter((p) => {
      if (p.archived) {
        const archiveDate = new Date(p.archiveYear, p.archiveMonth - 1);
        return archiveDate > new Date(selectedYear, 11, 31);
      }
      return true;
    });
    filtered = filtered.filter((p) => {
      const startYear = p.startYear ?? selectedYear;
      const startMonth = p.startMonth ?? 1;
      return startYear < selectedYear || (startYear === selectedYear && startMonth <= 12);
    });
    return filtered.sort((a, b) => {
      const fa = getFirmName(a.firmId);
      const fb = getFirmName(b.firmId);
      const firmCmp = fa.localeCompare(fb);
      if (firmCmp !== 0) return firmCmp;
      const nameCmp = getFirstNameWord(a.name).localeCompare(getFirstNameWord(b.name));
      if (nameCmp !== 0) return nameCmp;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [people, selectedFirm, selectedYear, firms]);

  const handleExportToExcel = async () => {
    const week = weeks[selectedExportWeek];
    if (!week) {
      alert('Nema dostupnog tjedna za izvoz.');
      return;
    }

    showLoading('Izvoz u Excel...');
    try {
      const workbook = new ExcelJS.Workbook();
      addSatiWeekWorksheet(workbook, week, filteredPeopleForExport, dailyMetricsData);
      const buffer = await workbook.xlsx.writeBuffer();
      const firmSuffix = selectedFirm ? `_${getFirmName(selectedFirm).replace(/\s/g, '_')}` : '';
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `Sati_${selectedYear}_${week.label}${firmSuffix}.xlsx`
      );
    } catch (e) {
      console.error('Greška pri izvozu:', e);
      alert('Došlo je do greške prilikom izvoza u Excel.');
    } finally {
      hideLoading();
    }
  };

  const handleExportMonthAllWeeksToExcel = async () => {
    const month = exportMonthAllWeeks;
    const indices = getWeekIndicesForCalendarMonth(weeks, selectedYear, month);
    if (indices.length === 0) {
      alert(
        `Nema tjedana koji sadrže dane u ${MONTH_NAMES_LONG[month - 1]} ${selectedYear}.`
      );
      return;
    }

    showLoading('Izvoz mjeseca u Excel (jedan list)...');
    try {
      const workbook = new ExcelJS.Workbook();
      addSatiMonthWideWorksheet(
        workbook,
        indices,
        weeks,
        filteredPeopleForExport,
        dailyMetricsData,
        selectedYear,
        month,
        monthlyNightHoursMap,
        holidaysByDate,
        obracunRates
      );
      const buffer = await workbook.xlsx.writeBuffer();
      const firmSuffix = selectedFirm ? `_${getFirmName(selectedFirm).replace(/\s/g, '_')}` : '';
      const mm = String(month).padStart(2, '0');
      const monthSlug = MONTH_NAMES_FILE[month - 1] || `mjesec-${month}`;
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `Sati_${selectedYear}_${mm}_${monthSlug}_svi-tjedni${firmSuffix}.xlsx`
      );
    } catch (e) {
      console.error('Greška pri izvozu mjeseca:', e);
      alert('Došlo je do greške prilikom izvoza mjeseca u Excel.');
    } finally {
      hideLoading();
    }
  };

  if (!currentUser) {
    return <Login />;
  }

  return (
    <div className="sati-page">
      <div className="filter-form">
        <div className="filter-controls">
          <label>Godina:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {Array.from(
              { length: new Date().getFullYear() - 2022 + 2 },
              (_, i) => 2022 + i
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <label>Firma:</label>
          <select
            value={selectedFirm}
            onChange={(e) => setSelectedFirm(e.target.value)}
          >
            <option value="">Sve</option>
            {firms.filter((f) => f.id !== 'default').map((firm) => (
              <option key={firm.id} value={firm.id}>
                {firm.name}
              </option>
            ))}
          </select>
        </div>

        <div className="toolbar-buttons">
          <label className="export-week-label">
            Izvoz tjedan:
            <select
              value={selectedExportWeek}
              onChange={(e) => setSelectedExportWeek(parseInt(e.target.value, 10))}
              className="export-week-select"
            >
              {weeks.map((w, i) => (
                <option key={i} value={i}>
                  {w.label} ({w.dates[0]?.day}.{w.dates[0]?.month}.–{w.dates[6]?.day}.{w.dates[6]?.month}.)
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={handleExportToExcel}
            className="export-excel-button"
            type="button"
          >
            <FaFileExport />
            <span>Izvezi tjedan</span>
          </button>
          <label className="export-month-all-label">
            Cijeli mjesec:
            <select
              value={exportMonthAllWeeks}
              onChange={(e) => setExportMonthAllWeeks(parseInt(e.target.value, 10))}
              className="export-month-all-select"
              aria-label="Mjesec za izvoz svih tjedana"
            >
              {MONTH_NAMES_LONG.map((name, i) => (
                <option key={i + 1} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={handleExportMonthAllWeeksToExcel}
            className="export-month-excel-button"
            title={`Jedan list: svaka osoba jedan blok, stupci su tjedni (${MONTH_NAMES_LONG[exportMonthAllWeeks - 1]} ${selectedYear})`}
          >
            <FaFileExport />
            <span>Izvezi sve tjedne (mjesec)</span>
          </button>
          <button
            type="button"
            onClick={openObracunSettings}
            className="manage-firms-button"
            title="Postavi EUR/sat multiplikatore za Obracun"
          >
            Obračun postavke
          </button>
          <button
            onClick={() => setIsFirmManagerOpen(true)}
            className="manage-firms-button"
          >
            Upravljaj firmama
          </button>
          <button
            onClick={() => setIsArchivedPersonsModalOpen(true)}
            className="manage-firms-button"
          >
            Arhivirane osobe
          </button>
          <button
            onClick={() => setIsPersonFormOpen(true)}
            className="add-person-button"
          >
            Dodaj osobu
          </button>
          <button
            onClick={async () => {
              await ensureUploadedDaysHours();
              setIsUploadedDaysModalOpen(true);
            }}
            className="manage-firms-button"
          >
            Učitani dani
          </button>
        </div>
      </div>

      {/* Modal: Dodaj osobu */}
      <Modal
        isOpen={isPersonFormOpen}
        onRequestClose={() => setIsPersonFormOpen(false)}
        contentLabel="Dodaj osobu"
        className="modal"
        overlayClassName="overlay"
      >
        <button
          onClick={() => setIsPersonFormOpen(false)}
          className="close-modal-button"
        >
          ×
        </button>
        <PersonFormSati
          isOpen={isPersonFormOpen}
          onClose={() => setIsPersonFormOpen(false)}
          onAddPerson={addPerson}
          firms={firms}
        />
      </Modal>

      {/* Modal: Upravljaj firmama */}
      <FirmManager
        isOpen={isFirmManagerOpen}
        onClose={() => {
          setIsFirmManagerOpen(false);
          refreshData();
        }}
        firms={firms}
        setFirms={setFirms}
      />

      {/* Modal: Arhivirane osobe */}
      <Modal
        isOpen={isArchivedPersonsModalOpen}
        onRequestClose={() => setIsArchivedPersonsModalOpen(false)}
        contentLabel="Arhivirane osobe"
        className="modal archived-persons-modal"
        overlayClassName="overlay"
      >
        <div className="modal-header">
          <h2>Arhivirane osobe</h2>
          <button
            onClick={() => setIsArchivedPersonsModalOpen(false)}
            className="close-modal-button"
          >
            ×
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Ime</th>
              <th>Firma</th>
              <th>Arhivirano</th>
              <th>Akcija</th>
            </tr>
          </thead>
          <tbody>
            {people
              .filter((p) => p.archived)
              .sort((a, b) => {
                const dateA = new Date(a.archiveYear, a.archiveMonth - 1);
                const dateB = new Date(b.archiveYear, b.archiveMonth - 1);
                return dateB - dateA;
              })
              .map((person) => (
                <tr key={person.id}>
                  <td>{person.name}</td>
                  <td>{getFirmName(person.firmId)}</td>
                  <td>
                    {person.archiveMonth}/{person.archiveYear}
                  </td>
                  <td>
                    <button
                      className="manage-firms-button"
                      onClick={() => unarchivePerson(person.id)}
                    >
                      Odarhiviraj
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Modal>

      <UploadedDaysModal
        isOpen={isUploadedDaysModalOpen}
        onClose={() => setIsUploadedDaysModalOpen(false)}
        uploadedDaysHours={uploadedDaysHours}
        firms={firms}
        onDeleteUploadedDay={deleteUploadedDayHours}
      />

      <ObracunSettingsModal
        isOpen={isObracunSettingsOpen}
        onClose={() => setIsObracunSettingsOpen(false)}
        baselineRates={obracunModalBaseline}
        onSave={handleSaveObracunRates}
      />

      {showHoursTable ? (
        <HoursTable
          people={people}
          dailyMetricsData={dailyMetricsData}
          holidaysByDate={holidaysByDate}
          selectedYear={selectedYear}
          selectedFirm={selectedFirm}
          firms={firms}
          refreshData={refreshData}
          onExcelUpload={handleExcelUpload}
          onUpdatePerson={updatePerson}
          onArchivePerson={archivePerson}
        />
      ) : (
        <p className="hours-table-loading-hint">Priprema tablice sati…</p>
      )}
    </div>
  );
}

export default Sati;
